import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition, IListSettings } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBStringUtils from "./DBStringUtils";
import DBListManager from "./DBListManager";
import DBFieldManager from "./DBFieldManager";

export default class DBListSettings {

    public static EditingFieldSettingsName: string | null = null;

    // ============================================
    // LIST SETTINGS
    // ============================================

    public static OpenListSettings = (listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }

            DBState.EditingListId = listId;
            const settings: IListSettings = list.Settings || {} as IListSettings;

            const titleEl: HTMLElement | null = document.getElementById("listSettingsModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Settings: " + list.Title; }

            DBListSettings.PopulateVersioningSettings(settings);
            DBListSettings.PopulateGeneralSettings(settings);
            DBListSettings.PopulateContentTypeSettings(list);

            DBModalManager.OpenModal("listSettingsModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.OpenListSettings");
        }
    };

    private static PopulateVersioningSettings = (settings: IListSettings): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const enableVersioning: HTMLInputElement | null = document.getElementById("lsEnableVersioning") as HTMLInputElement;
            const majorVersionLimit: HTMLInputElement | null = document.getElementById("lsMajorVersionLimit") as HTMLInputElement;
            const enableMinorVersions: HTMLInputElement | null = document.getElementById("lsEnableMinorVersions") as HTMLInputElement;
            const enableModeration: HTMLInputElement | null = document.getElementById("lsEnableModeration") as HTMLInputElement;
            const forceCheckout: HTMLInputElement | null = document.getElementById("lsForceCheckout") as HTMLInputElement;

            if (enableVersioning !== null) { enableVersioning.checked = settings.EnableVersioning || false; }
            if (majorVersionLimit !== null) { majorVersionLimit.value = String(settings.MajorVersionLimit || 500); }
            if (enableMinorVersions !== null) { enableMinorVersions.checked = settings.EnableMinorVersions || false; }
            if (enableModeration !== null) { enableModeration.checked = settings.EnableModeration || false; }
            if (forceCheckout !== null) { forceCheckout.checked = settings.ForceCheckout || false; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.PopulateVersioningSettings");
        }
    };

    private static PopulateGeneralSettings = (settings: IListSettings): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const onQuickLaunch: HTMLInputElement | null = document.getElementById("lsOnQuickLaunch") as HTMLInputElement;
            const enableAttachments: HTMLInputElement | null = document.getElementById("lsEnableAttachments") as HTMLInputElement;
            const enableFolderCreation: HTMLInputElement | null = document.getElementById("lsEnableFolderCreation") as HTMLInputElement;
            const contentTypesEnabled: HTMLInputElement | null = document.getElementById("lsContentTypesEnabled") as HTMLInputElement;
            const disableGridEditing: HTMLInputElement | null = document.getElementById("lsDisableGridEditing") as HTMLInputElement;
            const hidden: HTMLInputElement | null = document.getElementById("lsHidden") as HTMLInputElement;
            const readSecurity: HTMLInputElement | null = document.getElementById("lsReadSecurity") as HTMLInputElement;
            const writeSecurity: HTMLInputElement | null = document.getElementById("lsWriteSecurity") as HTMLInputElement;
            const listExperience: HTMLInputElement | null = document.getElementById("lsListExperience") as HTMLInputElement;

            if (onQuickLaunch !== null) { onQuickLaunch.checked = settings.OnQuickLaunch || false; }
            if (enableAttachments !== null) { enableAttachments.checked = settings.EnableAttachments !== false; }
            if (enableFolderCreation !== null) { enableFolderCreation.checked = settings.EnableFolderCreation || false; }
            if (contentTypesEnabled !== null) { contentTypesEnabled.checked = settings.ContentTypesEnabled || false; }
            if (disableGridEditing !== null) { disableGridEditing.checked = settings.DisableGridEditing || false; }
            if (hidden !== null) { hidden.checked = settings.Hidden || false; }
            if (readSecurity !== null) { readSecurity.value = String(settings.ReadSecurity || 1); }
            if (writeSecurity !== null) { writeSecurity.value = String(settings.WriteSecurity || 1); }
            if (listExperience !== null) { listExperience.value = String(settings.ListExperience || 0); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.PopulateGeneralSettings");
        }
    };

    private static PopulateContentTypeSettings = (list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const useCtCheckbox: HTMLInputElement | null = document.getElementById("lsUseContentType") as HTMLInputElement;
            const ctNameInput: HTMLInputElement | null = document.getElementById("lsContentTypeName") as HTMLInputElement;
            const ctGroupInput: HTMLInputElement | null = document.getElementById("lsContentTypeGroup") as HTMLInputElement;
            const ctFields: HTMLElement | null = document.getElementById("lsContentTypeFields");
            const ctEnabledCheckbox: HTMLInputElement | null = document.getElementById("lsContentTypesEnabled") as HTMLInputElement;

            if (useCtCheckbox !== null) { useCtCheckbox.checked = list.UseContentType || false; }

            const autoCtName: string = DBStringUtils.GetContentTypeName({
                ...list,
                ContentTypeName: ""
            } as IListDefinition);

            if (ctNameInput !== null) {
                ctNameInput.value = list.ContentTypeName || "";
                ctNameInput.placeholder = "Auto: " + autoCtName;
            }
            if (ctGroupInput !== null) { ctGroupInput.value = list.ContentTypeGroup || "Custom Content Types"; }

            const isCtChecked: boolean = useCtCheckbox !== null && useCtCheckbox.checked;
            if (ctFields !== null) { ctFields.style.display = isCtChecked ? "block" : "none"; }

            DBListSettings.SyncContentTypeEnabled(isCtChecked, ctEnabledCheckbox);
            DBListSettings.SetupContentTypeToggle(useCtCheckbox, ctFields, ctEnabledCheckbox);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.PopulateContentTypeSettings");
        }
    };

    private static SyncContentTypeEnabled = (isCtChecked: boolean, ctEnabledCheckbox: HTMLInputElement | null): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (ctEnabledCheckbox === null) { return; }
            if (isCtChecked) {
                ctEnabledCheckbox.checked = true;
                ctEnabledCheckbox.disabled = true;
            } else {
                ctEnabledCheckbox.disabled = false;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SyncContentTypeEnabled");
        }
    };

    private static SetupContentTypeToggle = (
        useCtCheckbox: HTMLInputElement | null,
        ctFields: HTMLElement | null,
        ctEnabledCheckbox: HTMLInputElement | null
    ): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (useCtCheckbox === null) { return; }
            const HandleChange = (): void => {
                if (ctFields !== null) {
                    ctFields.style.display = useCtCheckbox.checked ? "block" : "none";
                }
                DBListSettings.SyncContentTypeEnabled(useCtCheckbox.checked, ctEnabledCheckbox);
            };
            useCtCheckbox.onchange = HandleChange;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SetupContentTypeToggle");
        }
    };

    public static SaveListSettings = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.EditingListId || "");
            if (list === null) { return; }

            const useContentType: boolean = (document.getElementById("lsUseContentType") as HTMLInputElement).checked;

            const settings: IListSettings = {
                EnableVersioning: (document.getElementById("lsEnableVersioning") as HTMLInputElement).checked,
                MajorVersionLimit: parseInt((document.getElementById("lsMajorVersionLimit") as HTMLInputElement).value) || 500,
                EnableMinorVersions: (document.getElementById("lsEnableMinorVersions") as HTMLInputElement).checked,
                EnableModeration: (document.getElementById("lsEnableModeration") as HTMLInputElement).checked,
                OnQuickLaunch: (document.getElementById("lsOnQuickLaunch") as HTMLInputElement).checked,
                EnableAttachments: (document.getElementById("lsEnableAttachments") as HTMLInputElement).checked,
                EnableFolderCreation: (document.getElementById("lsEnableFolderCreation") as HTMLInputElement).checked,
                ContentTypesEnabled: useContentType ? true : (document.getElementById("lsContentTypesEnabled") as HTMLInputElement).checked,
                DisableGridEditing: (document.getElementById("lsDisableGridEditing") as HTMLInputElement).checked,
                ForceCheckout: (document.getElementById("lsForceCheckout") as HTMLInputElement).checked,
                Hidden: (document.getElementById("lsHidden") as HTMLInputElement).checked,
                ReadSecurity: parseInt((document.getElementById("lsReadSecurity") as HTMLInputElement).value) || 1,
                WriteSecurity: parseInt((document.getElementById("lsWriteSecurity") as HTMLInputElement).value) || 1,
                ListExperience: parseInt((document.getElementById("lsListExperience") as HTMLInputElement).value) || 0
            };

            list.Settings = settings;
            list.UseContentType = useContentType;
            list.ContentTypeName = (document.getElementById("lsContentTypeName") as HTMLInputElement).value.trim();
            list.ContentTypeGroup = (document.getElementById("lsContentTypeGroup") as HTMLInputElement).value.trim() || "Custom Content Types";

            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.CloseModal("listSettingsModal");
            DBModalManager.ShowToast("List settings saved", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveListSettings");
        }
    };

    // ============================================
    // FIELD SETTINGS
    // ============================================

    public static OpenFieldSettings = (listId: string, internalName: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list === null) { return; }
            const field: IFieldDefinition | undefined = list.Properties[internalName];
            if (field === undefined) { return; }

            DBState.EditingListId = listId;
            DBListSettings.EditingFieldSettingsName = internalName;

            const titleEl: HTMLElement | null = document.getElementById("fieldSettingsModalTitle");
            if (titleEl !== null) { titleEl.textContent = "Settings: " + (field.Title || internalName); }

            const fieldType: string = field.Format || field.Type;
            const html: string = DBListSettings.BuildFieldSettingsHtml(field, fieldType);

            const content: HTMLElement | null = document.getElementById("fieldSettingsContent");
            if (content !== null) { content.innerHTML = html; }

            DBModalManager.OpenModal("fieldSettingsModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.OpenFieldSettings");
        }
    };

    private static BuildFieldSettingsHtml = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let html: string = "";
            html += DBListSettings.BuildLegendHtml();
            html += DBListSettings.BuildFormVisibilityHtml(field);
            html += DBListSettings.BuildValidationHtml(field);
            html += DBListSettings.BuildNoteSettings(field, fieldType);
            html += DBListSettings.BuildNumberSettings(field, fieldType);
            html += DBListSettings.BuildDateSettings(field, fieldType);
            html += DBListSettings.BuildChoiceSettings(field, fieldType);
            html += DBListSettings.BuildUserSettings(field, fieldType);
            html += DBListSettings.BuildLookupSettings(field, fieldType);
            html += DBListSettings.BuildTextSettings(field, fieldType);
            html += DBListSettings.BuildFormatterHtml(field);
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildFieldSettingsHtml");
            return "";
        }
    };

    private static BuildLegendHtml = (): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            return '<div style="padding: 6px 10px; background: var(--bg-tertiary, #f5f5f5); border: 1px solid var(--border-color, #ddd); border-radius: 6px; margin-bottom: 12px; font-size: 11px; color: var(--text-secondary, #666);">'
                + '<span class="ps1-xml">PS1 + XML</span> = PowerShell &amp; PnP XML (not in Site Script) &nbsp;'
                + '<span class="ps1-only">PS1</span> = PowerShell only'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildLegendHtml");
            return "";
        }
    };

    private static BuildFormVisibilityHtml = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const displayChecked: string = field.ShowInDisplayForm !== false ? " checked" : "";
            const editChecked: string = field.ShowInEditForm !== false ? " checked" : "";
            const newChecked: string = field.ShowInNewForm !== false ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Form Visibility ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsShowInDisplayForm"' + displayChecked + '> Show in display form</label></div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsShowInEditForm"' + editChecked + '> Show in edit form</label></div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsShowInNewForm"' + newChecked + '> Show in new form</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildFormVisibilityHtml");
            return "";
        }
    };

    private static BuildValidationHtml = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const formulaVal: string = DBStringUtils.EscapeHtml(field.ValidationFormula || "");
            const messageVal: string = DBStringUtils.EscapeHtml(field.ValidationMessage || "");
            const uniqueChecked: string = field.EnforceUniqueValues ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Validation ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Validation formula</label></div>'
                + '<div style="margin-bottom: 8px;"><input type="text" class="form-input" id="fsValidationFormula" value="' + formulaVal + '" placeholder="e.g., =[Field]>0" style="font-size: 13px;"></div>'
                + '<div class="settings-row"><label>Error message</label></div>'
                + '<div><input type="text" class="form-input" id="fsValidationMessage" value="' + messageVal + '" placeholder="Message shown when validation fails" style="font-size: 13px;"></div>'
                + '<div class="settings-row" style="margin-top: 8px;"><label><input type="checkbox" id="fsEnforceUniqueValues"' + uniqueChecked + '> Enforce unique values</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildValidationHtml");
            return "";
        }
    };

    private static BuildNoteSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (fieldType !== "note" && fieldType !== "rich-text") { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const numLines: string = String(field.NumberOfLines || 6);
            const richChecked: string = field.RichText ? " checked" : "";
            const appendChecked: string = field.AppendOnly ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Multiline Text Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Number of lines</label>'
                + '<input type="number" id="fsNumberOfLines" value="' + numLines + '" min="1" max="100"></div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsRichText"' + richChecked + '> Enable rich text</label></div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsAppendOnly"' + appendChecked + '> Append only (comments mode)</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildNoteSettings");
            return "";
        }
    };

    private static BuildNumberSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const isNumber: boolean = fieldType === "number" || field.Type === "number" || field.Type === "integer";
            if (!isNumber) { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const minVal: string = field.MinimumValue !== undefined && field.MinimumValue !== null ? String(field.MinimumValue) : "";
            const maxVal: string = field.MaximumValue !== undefined && field.MaximumValue !== null ? String(field.MaximumValue) : "";
            const pctChecked: string = field.ShowAsPercentage ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Number Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Minimum value</label>'
                + '<input type="number" id="fsMinimumValue" value="' + minVal + '" placeholder="None" style="width: 120px;"></div>'
                + '<div class="settings-row"><label>Maximum value</label>'
                + '<input type="number" id="fsMaximumValue" value="' + maxVal + '" placeholder="None" style="width: 120px;"></div>'
                + DBListSettings.BuildDecimalPlacesSelect(field)
                + '<div class="settings-row"><label><input type="checkbox" id="fsShowAsPercentage"' + pctChecked + '> Show as percentage</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildNumberSettings");
            return "";
        }
    };

    private static BuildDecimalPlacesSelect = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const df: string = field.DisplayFormat || "";
            let html: string = '<div class="settings-row"><label>Decimal places</label>'
                + '<select id="fsDisplayFormat" style="width: 120px;">';

            const autoSelected: string = (df === "-1" || df === "" || df === undefined) ? " selected" : "";
            html += '<option value="-1"' + autoSelected + '>Automatic</option>';

            for (let i: number = 0; i <= 5; i++) {
                const isSelected: string = df === String(i) ? " selected" : "";
                html += '<option value="' + i + '"' + isSelected + '>' + i + '</option>';
            }

            html += '</select></div>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildDecimalPlacesSelect");
            return "";
        }
    };

    private static BuildDateSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (fieldType !== "date-time" && fieldType !== "date") { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const df: string = field.DisplayFormat || "";
            const fdf: string = field.FriendlyDisplayFormat || "";

            const dateOnlySel: string = !df || df === "0" ? " selected" : "";
            const dateTimeSel: string = df === "1" ? " selected" : "";
            const standardSel: string = !fdf || fdf === "0" ? " selected" : "";
            const friendlySel: string = fdf === "1" ? " selected" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Date/Time Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Format</label>'
                + '<select id="fsDateDisplayFormat" style="width: 140px;">'
                + '<option value="0"' + dateOnlySel + '>Date Only</option>'
                + '<option value="1"' + dateTimeSel + '>Date &amp; Time</option>'
                + '</select></div>'
                + '<div class="settings-row"><label>Friendly display</label>'
                + '<select id="fsFriendlyDisplayFormat" style="width: 140px;">'
                + '<option value="0"' + standardSel + '>Standard</option>'
                + '<option value="1"' + friendlySel + '>Friendly (relative)</option>'
                + '</select></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildDateSettings");
            return "";
        }
    };

    private static BuildChoiceSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (fieldType !== "choice" && fieldType !== "multichoice") { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const fillChecked: string = field.FillInChoice ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Choice Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsFillInChoice"' + fillChecked + '> Allow fill-in choices</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildChoiceSettings");
            return "";
        }
    };

    private static BuildUserSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (fieldType !== "user") { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const sm: string = field.SelectionMode || "";
            const peopleSel: string = !sm || sm === "0" ? " selected" : "";
            const groupsSel: string = sm === "1" ? " selected" : "";
            const multiChecked: string = field.AllowMultipleValues ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">User Field Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Selection mode</label>'
                + '<select id="fsSelectionMode" style="width: 160px;">'
                + '<option value="0"' + peopleSel + '>People only</option>'
                + '<option value="1"' + groupsSel + '>People and groups</option>'
                + '</select></div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsAllowMultipleValues"' + multiChecked + '> Allow multiple selections</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildUserSettings");
            return "";
        }
    };

    private static BuildLookupSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            if (fieldType !== "lookup") { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const multiChecked: string = field.AllowMultipleValues ? " checked" : "";

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Lookup Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label><input type="checkbox" id="fsAllowMultipleValues"' + multiChecked + '> Allow multiple selections</label></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildLookupSettings");
            return "";
        }
    };

    private static BuildTextSettings = (field: IFieldDefinition, fieldType: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const isPlainText: boolean = fieldType === "text" || (field.Type === "string" && !field.Format && (field.Enum === null || field.Enum === undefined || field.Enum.length === 0));
            if (!isPlainText) { return ""; }
            const ps1XmlBadge: string = '<span class="ps1-xml" title="Included in PowerShell and PnP XML output, not supported by Site Script">PS1 + XML</span>';
            const maxLen: string = String(field.MaxLength || 255);

            return '<div class="settings-group" style="margin-bottom: 12px;">'
                + '<div class="settings-group-title">Text Settings ' + ps1XmlBadge + '</div>'
                + '<div class="settings-row"><label>Max length</label>'
                + '<input type="number" id="fsMaxLength" value="' + maxLen + '" min="1" max="255"></div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildTextSettings");
            return "";
        }
    };

    private static BuildFormatterHtml = (field: IFieldDefinition): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const ps1Badge: string = '<span class="ps1-only" title="Only included in PowerShell output">PS1</span>';
            const formatterVal: string = DBStringUtils.EscapeHtml(field.CustomFormatter || "");

            return '<div class="settings-group">'
                + '<div class="settings-group-title">Column Formatting (JSON) ' + ps1Badge + '</div>'
                + '<textarea class="form-textarea" id="fsCustomFormatter" rows="4" placeholder=\'{"$schema":"..."}\' style="font-family: \'Courier New\', monospace; font-size: 11px;">' + formatterVal + '</textarea>'
                + '<div class="settings-hint">JSON column formatting for modern experience</div>'
                + '</div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.BuildFormatterHtml");
            return "";
        }
    };

    // ============================================
    // SAVE FIELD SETTINGS
    // ============================================

    public static SaveFieldSettings = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.EditingListId || "");
            if (list === null) { return; }
            if (DBListSettings.EditingFieldSettingsName === null) { return; }
            const field: IFieldDefinition | undefined = list.Properties[DBListSettings.EditingFieldSettingsName];
            if (field === undefined) { return; }

            const fieldType: string = field.Format || field.Type;

            DBListSettings.SaveFormVisibility(field);
            DBListSettings.SaveValidation(field);
            DBListSettings.SaveTypeSpecificSettings(field, fieldType);
            DBListSettings.SaveFormatter(field);
            DBListSettings.CleanUndefined(field);

            DBState.SaveState();
            DBFieldManager.RenderFields(list);
            DBModalManager.CloseModal("fieldSettingsModal");
            DBModalManager.ShowToast("Field settings saved", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveFieldSettings");
        }
    };

    private static SaveFormVisibility = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            field.ShowInDisplayForm = (document.getElementById("fsShowInDisplayForm") as HTMLInputElement).checked;
            field.ShowInEditForm = (document.getElementById("fsShowInEditForm") as HTMLInputElement).checked;
            field.ShowInNewForm = (document.getElementById("fsShowInNewForm") as HTMLInputElement).checked;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveFormVisibility");
        }
    };

    private static SaveValidation = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const formula: string = (document.getElementById("fsValidationFormula") as HTMLInputElement).value.trim();
            const message: string = (document.getElementById("fsValidationMessage") as HTMLInputElement).value.trim();
            const unique: boolean = (document.getElementById("fsEnforceUniqueValues") as HTMLInputElement).checked;

            (field as any).ValidationFormula = formula || undefined;
            (field as any).ValidationMessage = message || undefined;
            (field as any).EnforceUniqueValues = unique || undefined;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveValidation");
        }
    };

    private static SaveTypeSpecificSettings = (field: IFieldDefinition, fieldType: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (fieldType === "note" || fieldType === "rich-text") {
                DBListSettings.SaveNoteFieldSettings(field);
            }
            if (fieldType === "number" || field.Type === "number" || field.Type === "integer") {
                DBListSettings.SaveNumberFieldSettings(field);
            }
            if (fieldType === "date-time" || fieldType === "date") {
                DBListSettings.SaveDateFieldSettings(field);
            }
            if (fieldType === "choice" || fieldType === "multichoice") {
                DBListSettings.SaveChoiceFieldSettings(field);
            }
            if (fieldType === "user" || fieldType === "lookup") {
                DBListSettings.SaveUserFieldSettings(field, fieldType);
            }
            if (fieldType === "text" || (field.Type === "string" && !field.Format && (field.Enum === null || field.Enum === undefined || field.Enum.length === 0))) {
                DBListSettings.SaveTextFieldSettings(field);
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveTypeSpecificSettings");
        }
    };

    private static SaveNoteFieldSettings = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            (field as any).NumberOfLines = parseInt((document.getElementById("fsNumberOfLines") as HTMLInputElement).value) || 6;
            (field as any).RichText = (document.getElementById("fsRichText") as HTMLInputElement).checked || undefined;
            (field as any).AppendOnly = (document.getElementById("fsAppendOnly") as HTMLInputElement).checked || undefined;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveNoteFieldSettings");
        }
    };

    private static SaveNumberFieldSettings = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const minVal: string = (document.getElementById("fsMinimumValue") as HTMLInputElement).value;
            const maxVal: string = (document.getElementById("fsMaximumValue") as HTMLInputElement).value;
            (field as any).MinimumValue = minVal !== "" ? parseFloat(minVal) : undefined;
            (field as any).MaximumValue = maxVal !== "" ? parseFloat(maxVal) : undefined;
            const df: number = parseInt((document.getElementById("fsDisplayFormat") as HTMLInputElement).value);
            (field as any).DisplayFormat = df >= 0 ? String(df) : undefined;
            (field as any).ShowAsPercentage = (document.getElementById("fsShowAsPercentage") as HTMLInputElement).checked || undefined;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveNumberFieldSettings");
        }
    };

    private static SaveDateFieldSettings = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const df: number = parseInt((document.getElementById("fsDateDisplayFormat") as HTMLInputElement).value);
            (field as any).DisplayFormat = df ? String(df) : undefined;
            const fdf: number = parseInt((document.getElementById("fsFriendlyDisplayFormat") as HTMLInputElement).value);
            (field as any).FriendlyDisplayFormat = fdf ? String(fdf) : undefined;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveDateFieldSettings");
        }
    };

    private static SaveChoiceFieldSettings = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            (field as any).FillInChoice = (document.getElementById("fsFillInChoice") as HTMLInputElement).checked || undefined;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveChoiceFieldSettings");
        }
    };

    private static SaveUserFieldSettings = (field: IFieldDefinition, fieldType: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const amvEl: HTMLInputElement | null = document.getElementById("fsAllowMultipleValues") as HTMLInputElement;
            if (amvEl !== null) {
                (field as any).AllowMultipleValues = amvEl.checked || undefined;
            }
            if (fieldType === "user") {
                const smVal: number = parseInt((document.getElementById("fsSelectionMode") as HTMLInputElement).value);
                (field as any).SelectionMode = smVal ? String(smVal) : undefined;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveUserFieldSettings");
        }
    };

    private static SaveTextFieldSettings = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const maxLenEl: HTMLInputElement | null = document.getElementById("fsMaxLength") as HTMLInputElement;
            if (maxLenEl !== null) {
                const maxLen: number = parseInt(maxLenEl.value);
                if (maxLen && maxLen !== 255) {
                    (field as any).MaxLength = maxLen;
                } else {
                    delete (field as any).MaxLength;
                }
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveTextFieldSettings");
        }
    };

    private static SaveFormatter = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const formatterEl: HTMLTextAreaElement | null = document.getElementById("fsCustomFormatter") as HTMLTextAreaElement;
            if (formatterEl !== null) {
                (field as any).CustomFormatter = formatterEl.value.trim() || undefined;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.SaveFormatter");
        }
    };

    private static CleanUndefined = (field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldAny: any = field as any;
            const keys: string[] = Object.keys(fieldAny);
            const RemoveUndefined = (key: string): void => {
                if (fieldAny[key] === undefined) {
                    delete fieldAny[key];
                }
            };
            keys.forEach(RemoveUndefined);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBListSettings.CleanUndefined");
        }
    };
}
